
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Settings, Bell, MessageSquare, Gamepad2, Shield, CreditCard, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { NotificationType, ROLE_NOTIFICATION_TYPES } from '@/types/enhancedNotifications';
import { notificationPreferencesService, NotificationPreference } from '@/services/notificationPreferencesService';
import NotificationPreferenceItem from './NotificationPreferenceItem';

interface NotificationCategory {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  types: NotificationType[];
}

const NotificationPreferencesSection: React.FC = () => {
  const { user: supabaseUser, profile } = useAuth();
  const { user: internalUser } = useInternalAuth();
  
  const [preferences, setPreferences] = useState<NotificationPreference[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const user = supabaseUser || internalUser;
  const isInternalUser = !!internalUser && !supabaseUser;
  const userType = isInternalUser ? 'internal' : 'supabase';
  const userRole = isInternalUser ? internalUser?.role : profile?.role;

  // Get notification type details
  const getNotificationDetails = (type: NotificationType) => {
    const details = {
      mention: { title: 'Mentions', description: 'When someone mentions you in chat' },
      message: { title: 'Messages', description: 'New messages in your chat groups' },
      venue_message: { title: 'Venue Messages', description: 'Messages from venue managers' },
      game_notification: { title: 'Game Updates', description: 'Updates about games and matches' },
      score_update: { title: 'Score Updates', description: 'Live score updates during games' },
      tournament_update: { title: 'Tournament Updates', description: 'Tournament brackets and results' },
      new_follower: { title: 'New Followers', description: 'When someone follows you' },
      follow_request: { title: 'Follow Requests', description: 'Pending follow requests' },
      team_invite: { title: 'Team Invites', description: 'Invitations to join teams' },
      sign_in: { title: 'Sign In Alerts', description: 'Login notifications for security' },
      account_blocked: { title: 'Account Blocked', description: 'Account security alerts' },
      account_disabled: { title: 'Account Disabled', description: 'Account status changes' },
      account_investigation: { title: 'Account Investigation', description: 'Security investigation notices' },
      password_changed: { title: 'Password Changed', description: 'Password change confirmations' },
      login_attempt: { title: 'Login Attempts', description: 'Suspicious login attempts' },
      challenge: { title: 'Challenges', description: 'New coaching challenges' },
      violation: { title: 'Violations', description: 'Rule violation reports' },
      rule_update: { title: 'Rule Updates', description: 'Changes to rules and regulations' },
      injury_report: { title: 'Injury Reports', description: 'Player injury notifications' },
      new_signup: { title: 'New Signups', description: 'New player registrations' },
      needs_attention: { title: 'Needs Attention', description: 'Items requiring your attention' },
      payment_due: { title: 'Payment Due', description: 'Payment reminders and due dates' },
      parent_notification: { title: 'Parent Notifications', description: 'Updates for parents' },
      team_update: { title: 'Team Updates', description: 'General team announcements' },
      practice_reminder: { title: 'Practice Reminders', description: 'Upcoming practice sessions' },
      performance_update: { title: 'Performance Updates', description: 'Your performance analytics' },
      achievement: { title: 'Achievements', description: 'New achievements and milestones' },
      team_selection: { title: 'Team Selection', description: 'Team roster selections' },
      child_activity: { title: 'Child Activity', description: 'Your child\'s sports activities' },
      permission_request: { title: 'Permission Requests', description: 'Requests requiring parental consent' },
      emergency_contact: { title: 'Emergency Contact', description: 'Emergency notifications' },
      payment_reminder: { title: 'Payment Reminders', description: 'Payment due reminders' },
      booking_request: { title: 'Booking Requests', description: 'Facility booking requests' },
      facility_update: { title: 'Facility Updates', description: 'Venue facility changes' },
      maintenance_notice: { title: 'Maintenance Notices', description: 'Scheduled maintenance alerts' },
      system_update: { title: 'System Updates', description: 'Platform updates and changes' },
      maintenance: { title: 'Maintenance', description: 'System maintenance notifications' },
      policy_update: { title: 'Policy Updates', description: 'Terms and policy changes' },
      feature_announcement: { title: 'Feature Announcements', description: 'New feature releases' }
    };
    
    return details[type] || { title: type, description: `${type} notifications` };
  };

  // Define categories based on user role
  const getNotificationCategories = (): NotificationCategory[] => {
    if (!userRole) return [];
    
    const roleTypes = ROLE_NOTIFICATION_TYPES[userRole] || [];
    
    const categories: NotificationCategory[] = [
      {
        id: 'chat',
        title: 'Lockeroom & Communication',
        icon: MessageSquare,
        types: roleTypes.filter(type => ['mention', 'message', 'venue_message'].includes(type))
      },
      {
        id: 'game',
        title: 'Game & Sports',
        icon: Gamepad2,
        types: roleTypes.filter(type => ['game_notification', 'score_update', 'tournament_update'].includes(type))
      },
      {
        id: 'social',
        title: 'Social & Teams',
        icon: Bell,
        types: roleTypes.filter(type => ['new_follower', 'follow_request', 'team_invite', 'team_update'].includes(type))
      },
      {
        id: 'account',
        title: 'Account & Security',
        icon: Shield,
        types: roleTypes.filter(type => ['sign_in', 'account_blocked', 'account_disabled', 'account_investigation', 'password_changed', 'login_attempt'].includes(type))
      },
      {
        id: 'role_specific',
        title: `${userRole.charAt(0).toUpperCase() + userRole.slice(1)} Specific`,
        icon: Settings,
        types: roleTypes.filter(type => ![
          'mention', 'message', 'venue_message', 'game_notification', 'score_update', 'tournament_update',
          'new_follower', 'follow_request', 'team_invite', 'team_update', 'sign_in', 'account_blocked',
          'account_disabled', 'account_investigation', 'password_changed', 'login_attempt', 'payment_due'
        ].includes(type))
      },
      {
        id: 'payment',
        title: 'Payments & Billing',
        icon: CreditCard,
        types: roleTypes.filter(type => ['payment_due', 'payment_reminder'].includes(type))
      }
    ];

    return categories.filter(category => category.types.length > 0);
  };

  // Load user preferences
  const loadPreferences = async () => {
    if (!user || !userRole) return;

    try {
      setLoading(true);
      let userPreferences = await notificationPreferencesService.getUserPreferences(user.id, userType);
      
      // If no preferences exist, create defaults
      if (userPreferences.length === 0) {
        await notificationPreferencesService.createDefaultPreferences(user.id, userType, userRole);
        userPreferences = await notificationPreferencesService.getUserPreferences(user.id, userType);
      }
      
      setPreferences(userPreferences);
    } catch (error) {
      console.error('Error loading preferences:', error);
      toast.error('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  // Update individual preference
  const updatePreference = async (notificationType: NotificationType, updates: { enabled?: boolean; push_enabled?: boolean }) => {
    if (!user) return;

    try {
      setUpdating(true);
      const success = await notificationPreferencesService.updatePreference(user.id, userType, notificationType, updates);
      
      if (success) {
        setPreferences(prev => prev.map(pref => 
          pref.notification_type === notificationType 
            ? { ...pref, ...updates }
            : pref
        ));
        toast.success('Preference updated');
      } else {
        toast.error('Failed to update preference');
      }
    } catch (error) {
      console.error('Error updating preference:', error);
      toast.error('Failed to update preference');
    } finally {
      setUpdating(false);
    }
  };

  // Update category preferences
  const updateCategoryPreferences = async (categoryTypes: NotificationType[], updates: { enabled?: boolean; push_enabled?: boolean }) => {
    if (!user) return;

    try {
      setUpdating(true);
      const success = await notificationPreferencesService.updateCategoryPreferences(user.id, userType, categoryTypes, updates);
      
      if (success) {
        setPreferences(prev => prev.map(pref => 
          categoryTypes.includes(pref.notification_type)
            ? { ...pref, ...updates }
            : pref
        ));
        toast.success('Category preferences updated');
      } else {
        toast.error('Failed to update category preferences');
      }
    } catch (error) {
      console.error('Error updating category preferences:', error);
      toast.error('Failed to update category preferences');
    } finally {
      setUpdating(false);
    }
  };

  // Get preference for a notification type
  const getPreference = (type: NotificationType) => {
    return preferences.find(pref => pref.notification_type === type);
  };

  // Check if all types in category have same setting
  const getCategoryState = (categoryTypes: NotificationType[], field: 'enabled' | 'push_enabled') => {
    const categoryPrefs = categoryTypes.map(type => getPreference(type)).filter(Boolean);
    if (categoryPrefs.length === 0) return { allEnabled: false, noneEnabled: true, mixed: false };
    
    const allEnabled = categoryPrefs.every(pref => pref?.[field]);
    const noneEnabled = categoryPrefs.every(pref => !pref?.[field]);
    
    return { allEnabled, noneEnabled, mixed: !allEnabled && !noneEnabled };
  };

  useEffect(() => {
    loadPreferences();
  }, [user, userRole]);

  if (loading) {
    return (
      <Card className="bg-gray-900/50 border border-gray-700">
        <CardHeader>
          <CardTitle className="text-xl text-sports-green flex items-center gap-2">
            <Settings className="h-6 w-6" />
            Notification Preferences
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-700 rounded w-3/4"></div>
            <div className="h-4 bg-gray-700 rounded w-1/2"></div>
            <div className="h-4 bg-gray-700 rounded w-5/6"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const categories = getNotificationCategories();

  return (
    <Card className="bg-gray-900/50 border border-gray-700">
      <CardHeader>
        <CardTitle className="text-xl text-sports-green flex items-center gap-2">
          <Settings className="h-6 w-6" />
          Notification Preferences
        </CardTitle>
        <p className="text-gray-400 text-sm">
          Customize which notifications you receive and how you receive them. Changes are saved automatically.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {categories.map(category => {
          const IconComponent = category.icon;
          const inAppState = getCategoryState(category.types, 'enabled');
          const pushState = getCategoryState(category.types, 'push_enabled');
          
          return (
            <div key={category.id} className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <IconComponent className="h-5 w-5 text-sports-green" />
                  <h3 className="text-lg font-medium text-white">{category.title}</h3>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-gray-300">All In-App</Label>
                    <Switch
                      checked={inAppState.allEnabled}
                      onCheckedChange={(checked) => updateCategoryPreferences(category.types, { enabled: checked })}
                      disabled={updating}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-gray-300">All Push</Label>
                    <Switch
                      checked={pushState.allEnabled}
                      onCheckedChange={(checked) => updateCategoryPreferences(category.types, { push_enabled: checked })}
                      disabled={updating}
                    />
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-800/30 rounded-lg p-4 space-y-2">
                {category.types.map(type => {
                  const pref = getPreference(type);
                  const details = getNotificationDetails(type);
                  
                  return (
                    <NotificationPreferenceItem
                      key={type}
                      notificationType={type}
                      title={details.title}
                      description={details.description}
                      inAppEnabled={pref?.enabled || false}
                      pushEnabled={pref?.push_enabled || false}
                      onInAppToggle={(enabled) => updatePreference(type, { enabled })}
                      onPushToggle={(enabled) => updatePreference(type, { push_enabled: enabled })}
                      disabled={updating}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
        
        {categories.length === 0 && (
          <div className="text-center py-8">
            <AlertTriangle className="h-12 w-12 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-400">No notification preferences available for your role.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationPreferencesSection;
